"use client";

import { Fragment } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { domId } from "@/lib/dom-id";
import { coerceLocale } from "@/i18n/locales";
import { withLocalePath } from "@/lib/locale-routing";

function prettifySegment(segment: string): string {
  return segment
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getSegmentLabel(segment: string, t: ReturnType<typeof useTranslations>) {
  switch (segment) {
    case "connectors":
      return t("segments.connectors");
    case "add-api":
      return t("segments.addApi");
    case "authentication":
      return t("segments.authentication");
    case "specs":
      return t("segments.specs");
    case "actions":
      return t("segments.actions");
    case "action":
      return t("segments.action");
    case "new":
      return t("segments.new");
    case "review":
      return t("segments.review");
    case "editor":
      return t("segments.editor");
    case "callback-success":
      return t("segments.callbackSuccess");
    case "error":
      return t("segments.error");
    default:
      return prettifySegment(segment);
  }
}

export default function PageBreadcrumb() {
  const pathname = usePathname();
  const locale = useLocale();
  const activeLocale = coerceLocale(locale);
  const t = useTranslations("AppShell");

  const segments = (pathname ?? "").split("/").filter(Boolean);
  const pageSegments = segments.slice(1);

  if (pageSegments.length === 0) return null;
  if (pageSegments[0] === "welcome" || pageSegments[0] === "error") return null;

  const breadcrumbs = pageSegments.map((segment, index) => {
    const href = withLocalePath(
      activeLocale,
      `/${pageSegments.slice(0, index + 1).join("/")}`,
    );
    const label = getSegmentLabel(segment, t);
    return { href, label };
  });

  return (
    <nav aria-label={t("breadcrumbAria")} className="mb-4">
      <ol className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
        <li>
          <Link
            data-testid={domId("lnk", "breadcrumb-home")}
            href={withLocalePath(activeLocale, "/")}
            className="transition"
          >
            {t("home")}
          </Link>
        </li>
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;
          return (
            <Fragment key={item.href}>
              <li aria-hidden="true" className="text-slate-300">
                /
              </li>
              <li>
                {isLast ? (
                  <span className="font-medium text-slate-700">{item.label}</span>
                ) : (
                  <Link
                    data-testid={domId("lnk", `breadcrumb-${index + 1}`)}
                    href={item.href}
                    className="transition"
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
